import React, { useEffect, useState } from 'react'
import { useTheme } from 'react-jss'
import { useAtom } from 'jotai'
import {
    Close,
    Root,
    Trigger,
    Overlay,
    Content,
    Description,
    Portal,
} from '@radix-ui/react-dialog'
import { Button } from '../../ui/Button/Button'
import { useStyles } from '../../ui/Dialog/Dialog'
import { takeCase } from '../../../api/salary'
import { colors } from '../../../theme/palette'
import { Close as CloseIcon, IkonPromptsucces } from '../../../assets/icons'
import { Case } from '../../../models/Case'
import { L } from '../../providers/Lokalise'
import { Typography } from '../../ui/Typography/Typography'
import { profileAtom } from '../../../api/profile/profile'
import CloseButton from '../../ui/Dialog/Close'
import Title from '../../ui/Dialog/Title'
import { useErrorDialog } from '../../../hooks/useErrorDialog'
import { ErrorDisplay } from './Contact/ContactDialog'

type TakeCaseProps = {
    currentCase: Case
    onSuccess?: () => void
    useRegularButton?: boolean
}

type TakeCaseContentViewProps = {
    currentCase: Case
    onSuccess?: () => void
    isOpen?: boolean
    handleClose?: () => void
}

export const TakeCaseContentView = ({
    currentCase,
    onSuccess,
    isOpen,
    handleClose,
}: TakeCaseContentViewProps): JSX.Element => {
    const [isLoading, setIsLoading] = useState(false)
    const theme = useTheme<Jss.Theme>()
    const iconBGColor = colors.blue300
    const iconBorder = colors.white
    const [locked, setLocked] = useState(false)
    const [caseTaken, setCaseTaken] = useState(false)
    const { showError, ErrorDialogComponent } = useErrorDialog() as any
    const classes = useStyles({
        ...{ iconBGColor, iconBorder },
        theme,
    })

    const handleOpen = async (): Promise<void> => {
        if (locked) return
        setLocked(true)
        setIsLoading(true)
        const response = await takeCase(
            currentCase.caseId,
            currentCase.caseRegistrationDate
        )
        if (response.error) {
            showError(<ErrorDisplay errors={response.error.errors} />)
            setIsLoading(false)
            setLocked(false)
            setCaseTaken(false)
            return
        }
        setIsLoading(false)
        setLocked(false)
        setCaseTaken(true)
    }

    useEffect(() => {
        if (isOpen) {
            handleOpen()
        }
    }, [isOpen])

    const handleButtonClick = (): void => {
        handleClose?.()
        setTimeout(() => {
            onSuccess?.()
        }, 0)
    }

    return (
        <div className={`${classes.container} withIcon`}>
            <div className={classes.circleIcon}>
                <IkonPromptsucces width="46px" fill={colors.green} />
            </div>
            <Title
                style={{ marginBottom: '30px' }}
                title={L('LØN_TAG_SAG_DIALOG_TITLE')}
            />
            <Typography
                tag="p"
                size="S"
                weight="bold"
                style={{ marginBottom: '10px' }}
            >
                {currentCase.newlyHired?.name}
            </Typography>
            <Typography tag="p" size="S" style={{ marginBottom: '30px' }}>
                {L('LØN_TAG_SAG_DIALOG_CASE_ID_LABEL')} {currentCase.caseNumber}
            </Typography>
            <Description style={{ marginBottom: '40px' }}>
                {L('LØN_TAG_SAG_DIALOG_DESCRIPTION')}
            </Description>
            {!isLoading && <CloseButton handleClose={handleClose} />}
            <Close asChild>
                <Button
                    size="xlarge"
                    type="primary"
                    loading={isLoading}
                    disabled={isLoading}
                    onClick={handleButtonClick}
                >
                    {L('LØN_TAG_SAG_DIALOG_BUTTON_LABEL')}
                </Button>
            </Close>
            <ErrorDialogComponent />
        </div>
    )
}

export const TakeCase: React.FC<TakeCaseProps> = ({
    currentCase,
    onSuccess,
    useRegularButton = false,
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const theme = useTheme<Jss.Theme>()
    const iconBGColor = colors.blue300
    const iconBorder = colors.white
    const [authProfile] = useAtom(profileAtom)

    const classes = useStyles({
        ...{ iconBGColor, iconBorder },
        theme,
    })

    const handleOpen = (): void => {
        setIsOpen(true)
    }

    const handleClose = (): void => {
        setIsOpen(false)
    }

    return (
        <Root
            open={isOpen}
            onOpenChange={(open) => (open ? handleOpen() : handleClose())}
        >
            <Trigger asChild>
                {!useRegularButton ? (
                    <Button
                        type="secondary"
                        size="medium"
                        style={{
                            flex: 1,
                            borderRadius: '0 0 0 8px',
                            border: 'none',
                            margin: '0',
                        }}
                        disabled={authProfile.isImitator}
                    >
                        {L('LØN_TAG_SAG_BUTTON_LABEL')}
                    </Button>
                ) : (
                    <Button
                        size="medium"
                        type="primary"
                        disabled={authProfile.isImitator}
                    >
                        {L('LØN_TAG_SAG_BUTTON_LABEL')}
                    </Button>
                )}
            </Trigger>
            <Portal>
                <Overlay className={classes.overlay} />
                <Content className={`${classes.overlayContainer} withIcon`}>
                    <TakeCaseContentView
                        currentCase={currentCase}
                        onSuccess={onSuccess}
                        isOpen={isOpen}
                        handleClose={handleClose}
                    />
                </Content>
            </Portal>
        </Root>
    )
}
